import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { ArrowLeft, UserPlus, Plus } from "lucide-react";

import { addContact } from "slices/thunk";

import { Avatar, AvatarFallback } from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";
import { cn } from "lib/utils";

interface ContactFormProps {
  onCancel: () => void;
  onSubmit: (contact: any) => void;
  onBackToContactList?: () => void;
}

const FIELDS: {
  field: string;
  label: string;
  required?: boolean;
  type?: string;
}[] = [
  { field: "displayName", label: "Display Name", required: true },
  { field: "firstName", label: "First Name" },
  { field: "lastName", label: "Last Name" },
  { field: "email", label: "Email", required: true, type: "email" },
  { field: "phone", label: "Phone", required: true },
  { field: "company", label: "Company" },
  { field: "address", label: "Address" },
];

const ContactForm: React.FC<ContactFormProps> = ({
  onCancel,
  onSubmit,
  onBackToContactList,
}) => {
  const dispatch = useDispatch<any>();

  const formik = useFormik({
    initialValues: {
      displayName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      description: "",
      company: "",
      address: "",
    },
    validationSchema: Yup.object({
      displayName: Yup.string().required("Display name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .test(
          "email-required-if-no-phone",
          "Either email or phone is required",
          function (value) {
            const { phone } = this.parent;
            return !!value || !!phone;
          }
        ),
      phone: Yup.string()
        .matches(
          /^\+1\d{10}$/,
          "Phone number must start with +1 (USA code) followed by 10 digits"
        )
        .test(
          "phone-required-if-no-email",
          "Either email or phone is required",
          function (value) {
            const { email } = this.parent;
            return !!value || !!email;
          }
        ),
    }),
    onSubmit: (values: any) => {
      const payload: any = {
        name: values.displayName,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        PhoneNumber: values.phone,
        description: values.description,
        company: values.company,
        address: values.address,
      };
      dispatch(addContact(payload))
        .then((response: any) => {
          onSubmit(response?.payload);
          formik.resetForm();
        })
        .catch((error: any) => {
          console.error("Failed to add contact:", error);
        });
    },
  });

  return (
    <div className="h-full overflow-y-auto bg-card">
      {/* Mobile back button */}
      {onBackToContactList ? (
        <div className="flex items-center border-b border-border/60 p-3 xl:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBackToContactList}
            aria-label="Back to contact list"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
      ) : null}

      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
        {/* Header */}
        <div className="mb-8 flex flex-col items-center text-center">
          <Avatar className="h-20 w-20 bg-primary/10">
            <AvatarFallback className="bg-transparent text-primary">
              <UserPlus className="h-7 w-7" />
            </AvatarFallback>
          </Avatar>
          <h2 className="mt-3 text-xl font-semibold tracking-tight text-foreground">
            Add new contact
          </h2>
          <p className="text-sm text-muted-foreground">
            Create a new contact in your directory.
          </p>
        </div>

        <Separator className="mb-6" />

        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {FIELDS.map(({ field, label, required, type }) => {
              const fieldKey = field as keyof typeof formik.values;
              const error =
                formik.touched[fieldKey] &&
                typeof formik.errors[fieldKey] === "string"
                  ? (formik.errors[fieldKey] as string)
                  : null;
              return (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={`new-${field}`}>
                    {label}
                    {required && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </Label>
                  <Input
                    id={`new-${field}`}
                    name={field}
                    type={type ?? "text"}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[fieldKey] || ""}
                    aria-invalid={!!error}
                    className={cn(error && "border-destructive/60")}
                  />
                  {error ? (
                    <p className="text-xs font-medium text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-description">Description</Label>
            <textarea
              id="new-description"
              name="description"
              placeholder="Enter description"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.description || ""}
              rows={4}
              className={cn(
                "flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm transition-all",
                "placeholder:text-muted-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-primary/40"
              )}
            />
          </div>

          <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              <Plus className="h-4 w-4" />
              Add contact
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
